import { useContext, useCallback } from 'react';
import format from 'string-template';
import * as Sentry from '@sentry/browser';

import FirebaseContext from '../../../../../context/FirebaseContext';
import useLogger from '../../../../../hooks/useLogger';
import { CoachingActivity } from '../../../../../utils/log';
import Exercise, { ExerciseType } from '../../../../Model/Exercise';
import useToast from '../../../../hooks/useToast';
import { enqueueVideoProcessing } from '../../../../utils/vimeo';
import ExternalCoachContext from '../../../../context/ExternalCoachContext';
import fieldName from '../ExerciseEditor/formFields';
import { SaveStatus } from './constants';
import {
  hasValidFields,
  getCardView,
  toPersistedTags,
} from './utils';
import texts from './texts';

/**
 * Save lifecycle for the bulk creator: per-card save/update against Firestore,
 * plus the footer's batch actions (Save Ready / Retry Failed).
 *
 * The hook owns no card state — it reads through the parent's refs so the
 * callbacks never go stale, and reports changes back via `updateCard`.
 */
const useSaveExercises = ({ cardsRef, existingExercisesRef, updateCard }) => {
  const { firebase: { remote } } = useContext(FirebaseContext);
  const { coachDoc } = useContext(ExternalCoachContext);
  const coachId = coachDoc?.id;
  const { showToast } = useToast();
  const { logCoachingActivity } = useLogger();

  // Field validity independent of the status machine — used for fresh saves,
  // save-failed retries, and auto-save alike.
  const canSaveCard = useCallback((card, index, allCards) => (
    hasValidFields(card, index, allCards, existingExercisesRef.current)
  ), [existingExercisesRef]);

  const saveCard = useCallback(async (cardId) => {
    const allCards = cardsRef.current;
    const index = allCards.findIndex((item) => item.id === cardId);
    const card = allCards[index];
    if (!card || card.save === SaveStatus.SAVING || !canSaveCard(card, index, allCards)) {
      return;
    }
    const isUpdate = card.save === SaveStatus.SAVED && !!card.exerciseId;
    const cardName = card.name.trim();

    updateCard(cardId, (item) => ({ ...item, save: SaveStatus.SAVING, justEntered: false }));

    try {
      const { persistedTags, persistedTagIds } = toPersistedTags(card.tags);

      let { exerciseId } = card;
      if (isUpdate) {
        const exerciseDoc = await Exercise.getExerciseById(exerciseId);
        await exerciseDoc.updateFields({
          [fieldName.NAME]: cardName,
          [fieldName.DESCRIPTION]: card.description,
          [fieldName.TAGS]: persistedTags,
          [fieldName.TAG_IDS]: persistedTagIds,
          [fieldName.MODIFIED_AT]: new Date(),
        });
        logCoachingActivity(CoachingActivity.UPDATED_EXERCISE, { exerciseId });
      } else if (!exerciseId) {
        // A retained exerciseId means the doc was created on a previous attempt
        // and only a later step failed — never create it twice.
        exerciseId = await Exercise.addDoc({
          [fieldName.NAME]: cardName,
          [fieldName.DESCRIPTION]: card.description,
          [fieldName.TYPE]: ExerciseType.CUSTOM,
          [fieldName.VIDEO_URL]: '',
          [fieldName.VIDEO_PREVIEW_URL]: '',
          [fieldName.VIDEO_PREVIEW_THUMBNAIL]: '',
          [fieldName.CURRENT]: true,
          [fieldName.TAGS]: persistedTags,
          [fieldName.TAG_IDS]: persistedTagIds,
          [fieldName.CREATED_BY]: coachId,
          [fieldName.CREATED_AT]: new Date(),
        });
        updateCard(cardId, (item) => ({ ...item, exerciseId }));
        logCoachingActivity(CoachingActivity.CREATED_EXERCISE, { exerciseId });
      }

      // Queue video processing once per card; retries skip it when already done.
      const videoId = card.upload && card.upload.videoId;
      if (videoId && !card.enqueued) {
        await enqueueVideoProcessing(videoId, coachId, exerciseId, remote);
        updateCard(cardId, (item) => ({ ...item, enqueued: true }));
      }

      updateCard(cardId, (item) => ({
        ...item,
        save: SaveStatus.SAVED,
        saveError: null,
        collapsed: true,
        justEntered: true,
      }));
      showToast(format(isUpdate ? texts.toastUpdated : texts.toastSaved, { name: cardName }));
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          message: 'Error occurred while saving bulk-created exercise',
          exercise: cardName,
          coachId,
        },
      });
      updateCard(cardId, (item) => ({
        ...item,
        // A failed update leaves the exercise saved as it was; a failed create
        // (or enqueue) surfaces the retryable save-failed state.
        save: isUpdate ? SaveStatus.SAVED : SaveStatus.SAVE_FAILED,
        saveError: error.message,
      }));
      showToast(format(texts.toastSaveFailed, { name: cardName }), { error: true });
    }
  }, [cardsRef, canSaveCard, coachId, remote, updateCard, showToast, logCoachingActivity]);

  const saveReadyCards = useCallback(() => {
    const allCards = cardsRef.current;
    allCards.forEach((card, index) => {
      // Save-failed cards are retried as part of the batch action so a mixed
      // ready + failed state never needs per-card retries first.
      const isRetryable = card.save === SaveStatus.SAVE_FAILED;
      if (isRetryable || getCardView(card, index, allCards, existingExercisesRef.current).ready) {
        saveCard(card.id);
      }
    });
  }, [cardsRef, existingExercisesRef, saveCard]);

  const retryFailedSaves = useCallback(() => {
    cardsRef.current.forEach((card) => {
      if (card.save === SaveStatus.SAVE_FAILED) {
        saveCard(card.id);
      }
    });
  }, [cardsRef, saveCard]);

  return { saveCard, saveReadyCards, retryFailedSaves };
};

export default useSaveExercises;
